import { useSingleCallResult } from 'lib/hooks/multicall'
import { useMemo, useCallback } from 'react'
import { useLiquidityApproval } from 'lib/hooks/useLiquidityApproval'
import { useHasPendingLiquidityApproval, useTransactionAdder } from '../state/transactions/hooks'
import { useERC721Contract } from './useContract'
import { TransactionType } from '../state/transactions/actions'

export enum ApprovalState {
  UNKNOWN = 'UNKNOWN',
  NOT_APPROVED = 'NOT_APPROVED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}


function useGetAndTrackApproval(getApproval: ReturnType<typeof useLiquidityApproval>[1]) {
  const addTransaction = useTransactionAdder()
  return useCallback(() => {
    return getApproval().then((pending) => {
      if (pending) {
        const { response, tokenAddress, spenderAddress: spender } = pending
        addTransaction(response, { type: TransactionType.APPROVAL, tokenAddress, spender })
      }
    })
  }, [addTransaction, getApproval])
}

export function useLiquiditySpender(token?: string, tokenId?: string): string | undefined {
  const contract = useERC721Contract(token, false)

  const inputs = useMemo(() => [tokenId], [tokenId])
  const spender = useSingleCallResult(contract, 'getApproved', inputs).result

  return useMemo(
    () => (token && spender ? spender.toString() : undefined),
    [token, spender]
  )
}

export function useApproveCallback(
  token:string,
  tokenId: string,
  spender?: string
): [ApprovalState, () => Promise<void>] {
  const [approval, getApproval] = useLiquidityApproval(token, tokenId, spender, useHasPendingLiquidityApproval)
  return [approval, useGetAndTrackApproval(getApproval)]
}
